import { useAuth } from '@/hooks/useAuth';
import { PSI_CONTRACTING_KEY } from './psi-contracting-query-key';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import {
  GetPsiProfileResponse,
  CreatePsiProfileRequest,
  CreatePsiProfileResponse,
  UpdatePsiProfileRequest,
  UpdatePsiProfileResponse,
  PsiProfileDTO,
  PsiContractingProfileDTO,
  PsiContractingIdentityDTO,
} from '@inversor-hub/types';
import { ME_QUERY_KEY } from './me';

export const PSI_PROFILE_KEY = 'psi-profile';

export function useGetPsiProfile() {
  return useQuery({
    queryKey: [PSI_PROFILE_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<GetPsiProfileResponse>(`/psis/profile`);
      return data.data as PsiProfileDTO | undefined;
    },
  });
}

export function useCreatePsiProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePsiProfileRequest) => {
      const { data } = await apiClient.post<CreatePsiProfileResponse>(`/psis/profile`, input);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PSI_PROFILE_KEY] });
      queryClient.invalidateQueries({ queryKey: [PSI_CONTRACTING_KEY] });
      queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] });
    },
  });
}

export function useUpdatePsiProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdatePsiProfileRequest) => {
      const { data } = await apiClient.put<UpdatePsiProfileResponse>(`/psis/profile`, input);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PSI_PROFILE_KEY] });
      queryClient.invalidateQueries({ queryKey: [PSI_CONTRACTING_KEY] });
    },
  });
}

export function useGetPsiContractingProfile(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [PSI_CONTRACTING_KEY, user?.sub],
    enabled: enabled && Boolean(user),
    queryFn: async () => {
      const response = await apiClient.get<{ data: PsiContractingProfileDTO }>(
        '/psis/profile/contracting'
      );
      return response.data.data;
    },
  });
}

export function useUpdatePsiContractingProfile() {
  const { user } = useAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (identity: PsiContractingIdentityDTO) => {
      const response = await apiClient.put<{ data: PsiContractingProfileDTO }>(
        '/psis/profile/contracting',
        identity
      );
      return response.data.data;
    },
    onSuccess: (data) => client.setQueryData([PSI_CONTRACTING_KEY, user?.sub], data),
  });
}
